import { useSyncExternalStore } from 'react';
import { apiJson } from '../api/http';
import { HELP_FEEDBACK_EMAIL } from '../../config';

/**
 * The workspace settings the app itself reads (Admin › Settings › General), from
 * GET /api/settings/public. Until they arrive, and if they never do, every value is
 * the one Kudos used before the setting existed, so nothing changes by itself.
 * The server keeps the same defaults (backend WorkspaceSettings.java).
 */
export interface PublicSettings {
  support_email: string;
  invite_validity_days: string;
  kudos_message_max_length: number;
  /** Kudos and comments wait for a moderator (ADR-043). */
  kudos_require_approval: boolean;
  event_default_start_time: string;
  event_default_duration_minutes: number;
  event_default_waitlist: boolean;
  event_default_rsvp_close: string;
  event_default_currency: string;
  beta_badge: boolean;
  beta_badge_label: string;
  welcome_note: string;
  /** The points levels, in order; the first starts at 0 (Admin › Settings › Kudos). */
  points_levels: { name: string; min: number }[];
}

export const BUILT_IN_SETTINGS: PublicSettings = {
  support_email: '',
  invite_validity_days: '30',
  kudos_message_max_length: 2000,
  kudos_require_approval: true,
  event_default_start_time: '09:00',
  event_default_duration_minutes: 60,
  event_default_waitlist: true,
  event_default_rsvp_close: '',
  event_default_currency: 'RON',
  beta_badge: true,
  beta_badge_label: '',
  welcome_note: '',
  points_levels: [
    { name: 'Newcomer', min: 0 },
    { name: 'Synapse', min: 500 },
    { name: 'Cortex', min: 1500 },
    { name: 'Singularity', min: 4000 },
  ],
};

let current: PublicSettings = BUILT_IN_SETTINGS;
let pending: Promise<PublicSettings> | null = null;
let loaded = false;
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((l) => l());
}

/** Fetched once per page load (and again after a super admin saves); never rejects. */
export function loadPublicSettings(): Promise<PublicSettings> {
  if (loaded) return Promise.resolve(current);
  if (!pending) {
    pending = apiJson('/api/settings/public', { auth: true })
      .then((data: any) => {
        current = { ...BUILT_IN_SETTINGS, ...(data && typeof data === 'object' ? data : {}) };
        loaded = true;
        emit();
        return current;
      })
      .catch(() => current)
      .finally(() => { pending = null; });
  }
  return pending;
}

/** After a save in the Admin Center: read them again so this tab shows the new values. */
export function refreshPublicSettings(): Promise<PublicSettings> {
  loaded = false;
  return loadPublicSettings();
}

/** For tests. */
export function resetPublicSettings(next: Partial<PublicSettings> | null = null) {
  current = next ? { ...BUILT_IN_SETTINGS, ...next } : BUILT_IN_SETTINGS;
  loaded = !!next;
  pending = null;
  emit();
}

// Unit tests set the values with resetPublicSettings instead of fetching them: a fetch
// started by any component that shows the beta tag would land in other tests' fetch mocks.
const AUTO_LOAD = process.env.NODE_ENV !== 'test';

function subscribe(listener: () => void) {
  listeners.add(listener);
  if (!loaded && AUTO_LOAD) loadPublicSettings();
  return () => { listeners.delete(listener); };
}

const snapshot = () => current;

/** The settings, re-rendering when they arrive. Built-in values meanwhile. */
export function usePublicSettings(): PublicSettings {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

/** Where "Report a problem" writes to: the workspace's support address, else the built-in one. */
export function supportEmail(s: PublicSettings): string {
  return (s.support_email || '').trim() || HELP_FEEDBACK_EMAIL;
}

/** The settings once they are in (at once in unit tests, which set them by hand). */
export function settingsReady(): Promise<PublicSettings> {
  return AUTO_LOAD ? loadPublicSettings() : Promise.resolve(current);
}

/** The values as they are right now, without waiting. */
export function publicSettingsNow(): PublicSettings {
  return current;
}
